import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '@/components/CenteredState';
import { usePermissions } from '@/core/hooks/usePermissions';
import { restoreState } from '@/modules/auth/redux/authSlice';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string | string[];
  loadingFallback?: React.ReactNode; // opsiyonel
}

const safeParseAuthState = () => {
  try {
    return JSON.parse(localStorage.getItem('authState') || '{}');
  } catch {
    return {};
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  loadingFallback,
}) => {
  const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  const location = useLocation();

  // 🔒 LocalStorage'dan auth oku (side-effect yok)
  const storageAuth = useMemo(() => safeParseAuthState(), []);
  const hasStorageTokens = Boolean(storageAuth?.accessToken && storageAuth?.refreshToken);

  // ✅ Restore işlemini sadece 1 kere dene
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!restoredRef.current && !isAuthenticated && hasStorageTokens) {
      restoredRef.current = true;
      dispatch(
        restoreState({
          ...storageAuth,
          isAuthenticated: true,
        })
      );
    }
  }, [dispatch, isAuthenticated, hasStorageTokens, storageAuth]);

  // ---------------------------
  // AUTH CHECK
  // ---------------------------
  if (!isAuthenticated && !hasStorageTokens) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ---------------------------
  // PERMISSION CHECK
  // ---------------------------
  const { hasPermission, permissionsLoading } = usePermissions();

  const requiresPermission = Boolean(permission);
  const hasAccess = !requiresPermission || hasPermission(permission!);

  // ✅ Permission gerekiyorsa loading bitmeden children'ı render ETME
  if (requiresPermission && permissionsLoading) {
    return loadingFallback ?? <CenteredSpinner size="viewport" />;
  }

  // ✅ Permission yoksa 403
  if (requiresPermission && !permissionsLoading && !hasAccess) {
    return <Navigate to="/403" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default React.memo(ProtectedRoute);
